import { type FC, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil, noop, omit } from 'lodash';
// Import Types
import type { CJVisualizationButtonRouteUIModel, PushNotificationSettingsCascadeConfig } from 'types';
// Import Components
import ValidatableDropdown from 'components/common/ValidatableDropdown';
import PushNotificationCascadeForm from 'components/Template/PushNotificationCascadeForm';
// Import Hooks
import useCJVisualizationButtonActions from 'hooks/UseCJVisualizationButtonActions';
// Import Constants
import { l } from 'constants/common';
// Import Styles
import styles from './customerJourneyVisualizationButtonRoute.module.scss';

interface Props {
    page?: Nullable<string>;
    extraInfo?: AnyObject;
    getUpdate?: (data: CJVisualizationButtonRouteUIModel) => void;
}

const CustomerJourneyVisualizationButtonRoute: FC<Props> = (props) => {
    const { page: defaultPage = null, extraInfo: defaultExtraInfo = {}, getUpdate = noop } = props;

    const { t } = useTranslation();
    const { actions, isLoading } = useCJVisualizationButtonActions();

    const [page, setPage] = useState<Nullable<string>>(defaultPage);
    const [extraInfo, setExtraInfo] = useState<AnyObject>(defaultExtraInfo ?? {});
    const [isExtraInfoValid, setIsExtraInfoValid] = useState<boolean>(false);

    const actionsData = useMemo(
        () => actions.map(({ Page, CascadeConfig }) => ({ value: Page, label: Page, CascadeConfig })),
        [actions],
    );

    const selectedAction = useMemo(() => actionsData.find((action) => action.value === page), [actionsData, page]);
    const cascade: Nullable<PushNotificationSettingsCascadeConfig> = selectedAction?.CascadeConfig ?? null;
    const hasCascade = !isEmpty(cascade?.Cascade);
    const isPageSelected = !isNil(page);
    const isPageAllowed = isLoading || isEmpty(actionsData) || !isNil(selectedAction);
    const isPageValid = isPageSelected && isPageAllowed;

    const getPageErrorText = (): string => {
        if (!isPageSelected) {
            return t(l.CannotBeEmpty, { fieldName: t(l.Action) });
        }
        return isPageAllowed ? '' : t(l.IsNotValid, { fieldName: t(l.Action) });
    };

    const pageChangeHandler = (item) => {
        setPage(item?.value ?? null);
        setExtraInfo({});
        setIsExtraInfoValid(false);
    };

    const extraInfoValidationChangeHandler = (isValid: boolean) => {
        setIsExtraInfoValid(isValid);
    };

    const extraInfoChangeHandler = (value: AnyObject) => {
        setExtraInfo(omit(value, 'type'));
    };

    useEffect(() => {
        getUpdate({
            page,
            extraInfo,
            isValid: isPageValid && !isLoading && (!hasCascade || isExtraInfoValid),
        });
        // DEPS: [getUpdate]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, extraInfo, isExtraInfoValid, hasCascade, isLoading, isPageValid]);

    return (
        <div className={styles.buttonRoute}>
            <ValidatableDropdown
                value={page}
                onChange={pageChangeHandler}
                data={actionsData}
                disabled={isLoading || (isEmpty(actionsData) && !isPageSelected)}
                clearable={true}
                placeholder={t(l.Action)}
                label={t(l.Action)}
                labelAppearance="swap"
                appearance="outline"
                isValid={isPageValid}
                errorText={getPageErrorText()}
            />
            {hasCascade && (
                <PushNotificationCascadeForm
                    key={page ?? undefined}
                    cascade={cascade}
                    value={extraInfo}
                    isLoading={isLoading}
                    onChange={extraInfoChangeHandler}
                    validationChange={extraInfoValidationChangeHandler}
                />
            )}
        </div>
    );
};

CustomerJourneyVisualizationButtonRoute.displayName = 'CustomerJourneyVisualizationButtonRoute';

export default memo(CustomerJourneyVisualizationButtonRoute);
